import { RootState } from "@/app/store";
import { useTicket } from "@/context/TicketContext";
import { Counter, Service, useGetTicketByUserIdQuery } from "@/services/LiveQueueService";
import { TicketStatus, Ticket } from "@/services/LiveQueueService";
import { useSelector } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import { Skeleton } from "@/components/ui/Skeleton"; // For skeleton loading
import { ExclamationTriangleIcon } from "@radix-ui/react-icons"; // For error icon
import { useNavigate } from "react-router-dom";
import DetailLogs from "@/components/admin/tickets/ticket-detail/AuditLog";
export default function PreviouslyServed() {
  const [openLog, setOpenLog] = useState(false);
  const [ticketId, setTicketId] = useState<undefined | Number>();

  const navigate = useNavigate();
  const { userId, currentTicket } = useTicket();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedCounter, setSelectedCounter] = useState<Counter | null>(null);
  const [selectService, setSelectService] = useState<Service | null>(null);


  const queryParams = useMemo(() => {
    const today = new Date();
    const localDate = today.toLocaleDateString("en-CA"); 
    const params = new URLSearchParams({
      date_filter: localDate,
      sort_by: "created_at",
      sort_order: "desc",
      skip: "0",
      search: "s",
      limit: "100",
      ...(userId && userId !== 0 && { user_id: userId.toString() }),
    });

    // Add multiple `status` parameters
    [
      TicketStatus.NO_SHOW,
      TicketStatus.IN_PROGRESS,
      TicketStatus.AWAITING_INFO,
      TicketStatus.HOLD,
      TicketStatus.CANCELLED,
      TicketStatus.COMPLETED,
      "served",
    ].forEach((status) => {
      params.append("status", status);
    });

    return params.toString();
  }, [userId, userInfo, currentTicket?.id]);

  const {
    data: ticketsData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetTicketByUserIdQuery({ query: queryParams });

  // Function to extract all tickets from the nested structure
  const extractAllTickets = useMemo(() => {
    if (!ticketsData || !Array.isArray(ticketsData)) return [];

    // Flatten the nested structure to get all tickets
    return Array.isArray(ticketsData)
      ? ticketsData.flatMap((department) =>
          department.counters.flatMap((counter: Counter) =>
            counter.tickets.map((ticket) => ({
              ...ticket,
              counterInfo: {
                id: counter.id,
                service: counter.service,
              },
              departmentInfo: {
                id: department.id,
                name: department.name,
              },
            }))
          )
        )
      : [];
  }, [ticketsData]);

  useEffect(() => {
    setSelectedTicket(null);
    setSelectedCounter(null);
    setSelectService(null);

    if (userId && userId !== 0) {
      refetch();
    }
  }, [userId, currentTicket?.id]);

  // Skeleton Loading
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-4">
        <h2 className="mb-4 text-base font-medium">Previously Served</h2>
        <div className="space-y-2">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="flex items-center justify-between rounded-lg bg-[#F8F9FB] px-3 py-2"
            >
              <div className="space-y-0.5">
                <Skeleton style={{ width: 100, height: 16 }} />
                <Skeleton style={{ width: 50, height: 14 }} />
              </div>
              <Skeleton style={{ width: 60, height: 20 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error Handling
  if (isError) {
    return (
      <div className="rounded-xl bg-white p-4">
        <h2 className="mb-4 text-base font-medium">Previously Served</h2>
        <div className="text-sm flex justify-center items-center flex-col">
          <ExclamationTriangleIcon className="h-4 w-4" />
          <p className="text-center">
            Failed to load tickets. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  // No Tickets Available
  if (
    !ticketsData ||
    !Array.isArray(ticketsData) ||
    ticketsData.length === 0 ||
    extractAllTickets.length === 0 ||
    userId === Number(0)
  ) {
    return (
      <div className="rounded-xl bg-white p-4">
        <h2 className="mb-4 text-base font-medium">Previously Served</h2>
        <div className="text-sm flex justify-center items-center flex-col">
          <p className="text-center">No previous tickets available.</p>
          <button
            className="mt-3 flex w-full items-center justify-center text-sm font-medium underline text-[#D92D20] hover:text-[#B3251A]"
            onClick={() => navigate("/admin/tickets")}
          >
            View All
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white p-4 ">
      <h2 className="mb-4 text-base font-medium">Previously Served</h2>
      <div className="space-y-2 max-h-56 overflow-auto custom-scrollbar h-fit">
        {userId != null && userId !== 0 && userId > 0 ? (
          extractAllTickets
            .filter(
              (ticket) =>
                userId &&
                userId != 0 &&
                Number(ticket.user_id) == userId &&
                ticket.number.startsWith("S-") &&
                ticket.status !== "CHECKED_IN" &&
                ticket.ticket_number !== currentTicket?.ticket_number &&
                ticket.parent_ticket_id === null
            )
            .map((ticket) => (
              <div
                key={ticket.id}
                className="flex items-center justify-between rounded-lg bg-[#F8F9FB] px-3 py-2 cursor-pointer hover:bg-[#F0F2F5]"
                onClick={() => {
                  setTicketId(Number(ticket.id));
                  setOpenLog(true);
                }}
              >
                <div className="space-y-0.5">
                  <div className="text-xs font-medium">
                    {ticket.user_details.first_name}{" "}
                    {ticket.user_details.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {ticket.number}
                  </div>
                </div>
                <div className="rounded-full bg-[#E7F6EC] px-2 py-0.5 text-xs font-medium text-[#0E8345]">
                  {ticket.status.replace(/_/g, " ")}
                </div>
              </div>
            ))
        ) : (
          <div className="text-center text-gray-500">
            No previous tickets for this user.
          </div>
        )}
      </div>
      <button
        className="mt-3 flex w-full items-center justify-center text-sm font-medium underline text-[#D92D20] hover:text-[#B3251A]"
        onClick={() => navigate("/admin/tickets")}
      >
        View All
      </button>
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={ticketId} />
    </div>
  );
}